import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CornerDownRight, Eye, EyeOff, Loader2, MessageCircle, Pencil, Trash2 } from 'lucide-react';
import type { Comment, Post } from '@shared/types';
import { NepaliTextarea, useTransliterationEnabled } from '@/components/NepaliInput';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, ApiClientError } from '@/lib/api';
import { useSession } from '@/lib/session';
import { cn, formatRelative, initials } from '@/lib/utils';
import { n, t } from '@/lib/i18n';

const MAX_BODY = 2000;

/**
 * The conversation under a piece.
 *
 * Signed-in only, deliberately — the same rule as likes. A reader here is an
 * account with a handle and usually writing of their own, which is what makes a
 * reply worth reading and moderation worth almost nothing.
 *
 * Loaded after the post rather than with it: a thread is below the fold by
 * definition, and a महाकाव्य's third page should not wait on it.
 */
export function Comments({ post }: { post: Post }) {
  const { viewer } = useSession();
  const [items, setItems] = useState<Comment[]>([]);
  const [total, setTotal] = useState(post.commentCount);
  const [open, setOpen] = useState(post.commentsOpen);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      setLoading(true);
      try {
        const thread = await api.comments(post.id, signal);
        setItems(thread.items);
        setTotal(thread.total);
        setOpen(thread.open);
      } catch (err) {
        if ((err as Error).name !== 'AbortError') setError(t('प्रतिक्रिया ल्याउन सकिएन।'));
      } finally {
        setLoading(false);
      }
    },
    [post.id],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  /*
   * Every mutation re-reads the thread rather than patching the array in place.
   * Hiding a comment changes what its replies do, deleting one takes them with
   * it, and the server is the only thing that knows what this viewer may now
   * see — a local splice would get one of those three wrong.
   */
  const reload = () => load();

  // A closed section with nothing in it is not a section: on a piece where the
  // writer never wanted comments, this should leave no trace at all.
  if (!open && total === 0 && !loading) return null;

  return (
    <section className="mt-12 border-t pt-8" aria-labelledby="comments-heading">
      <h2 id="comments-heading" className="np flex items-center gap-2 text-xl font-semibold">
        <MessageCircle className="size-5 text-muted-foreground" />
        {total > 0 ? t('{count} प्रतिक्रिया', { count: n(total) }) : t('प्रतिक्रिया')}
      </h2>

      {error && (
        <p className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      )}

      {open ? (
        viewer ? (
          <CommentForm
            placeholder={t('यो रचनाबारे केही लेख्नुहोस्…')}
            submitLabel={t('पठाउनुहोस्')}
            onSubmit={async (body) => {
              await api.addComment(post.id, { body });
              await reload();
            }}
            className="mt-5"
          />
        ) : (
          // One string, not a sentence cut around a link: the word order of the
          // two halves is not the same in both languages.
          <p className="np mt-5 rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
            <Link to="/login" className="text-primary underline underline-offset-4">
              {t('प्रतिक्रिया लेख्न लगइन गर्नुहोस्।')}
            </Link>
          </p>
        )
      ) : (
        <p className="np mt-5 text-sm text-muted-foreground">{t('यो रचनामा प्रतिक्रिया बन्द छ।')}</p>
      )}

      {loading && items.length === 0 ? (
        <div className="mt-8 flex justify-center">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        open && (
          <p className="np mt-8 text-sm text-muted-foreground">{t('पहिलो प्रतिक्रिया तपाईंकै होस्।')}</p>
        )
      ) : (
        <ul className="mt-8 space-y-6">
          {items.map((comment) => (
            <li key={comment.id}>
              <CommentRow comment={comment} canReply={open && Boolean(viewer)} onChanged={reload} />

              {comment.replies.length > 0 && (
                <ul className="mt-4 space-y-4 border-s ps-4">
                  {comment.replies.map((reply) => (
                    <li key={reply.id}>
                      <CommentRow comment={reply} canReply={false} onChanged={reload} />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * One comment, with whatever this viewer is allowed to do to it.
 *
 * The permissions come down with the comment (`canEdit`, `canDelete`,
 * `canModerate`) rather than being worked out here from ids and roles — the
 * rules live in the API, and a button this renders is one the API has already
 * agreed to.
 */
function CommentRow({
  comment,
  canReply,
  onChanged,
}: {
  comment: Comment;
  canReply: boolean;
  onChanged: () => Promise<void> | void;
}) {
  const [editing, setEditing] = useState(false);
  const [replying, setReplying] = useState(false);
  const [busy, setBusy] = useState(false);

  const name = comment.author.penName || comment.author.displayName;

  const act = async (run: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await run();
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className={cn('group', comment.status === 'hidden' && 'opacity-60')}>
      <div className="flex items-center gap-2.5">
        <Avatar className="size-7">
          {comment.author.avatarUrl ? <AvatarImage src={comment.author.avatarUrl} alt="" /> : null}
          <AvatarFallback className="text-[10px]">{initials(name)}</AvatarFallback>
        </Avatar>

        <div className="min-w-0">
          <Link to={`/author/${comment.author.handle}`} className="np text-sm font-medium hover:text-primary">
            {name}
          </Link>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span>{formatRelative(comment.createdAt)}</span>
            {comment.editedAt && <span className="np">· {t('सम्पादित')}</span>}
          </div>
        </div>

        {/* Only a moderator ever sees this — for everyone else the comment is
            simply absent from the thread. */}
        {comment.status === 'hidden' && (
          <Badge variant="muted" className="np ms-1">
            {t('लुकाइएको')}
          </Badge>
        )}
      </div>

      {editing ? (
        <CommentForm
          className="mt-2 ps-9"
          initial={comment.body}
          submitLabel={t('सुरक्षित गर्नुहोस्')}
          onCancel={() => setEditing(false)}
          onSubmit={async (body) => {
            await api.editComment(comment.id, body);
            setEditing(false);
            await onChanged();
          }}
        />
      ) : (
        // Plain text, rendered as a text node. Comments are never HTML, which
        // is why nothing here goes near dangerouslySetInnerHTML.
        <p className="np mt-2 whitespace-pre-wrap ps-9 text-[0.95rem] leading-relaxed">{comment.body}</p>
      )}

      {!editing && (
        <div className="mt-1.5 flex flex-wrap items-center gap-1 ps-9 text-xs">
          {canReply && (
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setReplying((on) => !on)}>
              <CornerDownRight />{t('जवाफ')}</Button>
          )}
          {comment.canEdit && (
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setEditing(true)}>
              <Pencil />{t('बदल्नुहोस्')}</Button>
          )}
          {comment.canModerate && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              disabled={busy}
              onClick={() =>
                act(() => api.setCommentStatus(comment.id, comment.status === 'hidden' ? 'visible' : 'hidden'))
              }
            >
              {comment.status === 'hidden' ? <Eye /> : <EyeOff />}
              {comment.status === 'hidden' ? t('देखाउनुहोस्') : t('लुकाउनुहोस्')}
            </Button>
          )}
          {comment.canDelete && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-destructive"
              disabled={busy}
              onClick={() => {
                if (!window.confirm(t('यो प्रतिक्रिया स्थायी रूपमा मेट्ने?'))) return;
                void act(() => api.deleteComment(comment.id));
              }}
            >
              <Trash2 />{t('मेट्नुहोस्')}</Button>
          )}
        </div>
      )}

      {replying && (
        <CommentForm
          className="mt-3 ps-9"
          placeholder={t('{name} लाई जवाफ…', { name })}
          submitLabel={t('पठाउनुहोस्')}
          onCancel={() => setReplying(false)}
          onSubmit={async (body) => {
            // Replies attach to the top-level comment: the server flattens a
            // reply-to-a-reply onto the same thread, and sending the parent it
            // will use keeps the optimistic view honest.
            await api.addComment(comment.postId, { body, parentId: comment.parentId ?? comment.id });
            setReplying(false);
            await onChanged();
          }}
        />
      )}
    </article>
  );
}

/**
 * The box, used for a new comment, a reply and an edit alike.
 *
 * Romanized input converts as you type, through the same transliteration the
 * editor uses and the same on/off preference — a reader who turns it off in one
 * place has turned it off everywhere.
 */
function CommentForm({
  initial = '',
  placeholder,
  submitLabel,
  onSubmit,
  onCancel,
  className,
}: {
  initial?: string;
  placeholder?: string;
  submitLabel: string;
  onSubmit: (body: string) => Promise<void>;
  onCancel?: () => void;
  className?: string;
}) {
  const [body, setBody] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transliteration] = useTransliterationEnabled();

  const submit = async () => {
    const text = body.trim();
    if (!text) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(text);
      setBody('');
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : t('पठाउन सकिएन।'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={className}>
      <NepaliTextarea
        value={body}
        onValueChange={setBody}
        enabled={transliteration}
        placeholder={placeholder}
        maxLength={MAX_BODY}
        rows={3}
        aria-label={submitLabel}
      />

      {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}

      <div className="mt-2 flex items-center gap-2">
        <Button size="sm" disabled={busy || !body.trim()} onClick={submit}>
          {busy ? <Loader2 className="animate-spin" /> : null}
          {submitLabel}
        </Button>
        {onCancel && (
          <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
            {t('रद्द')}
          </Button>
        )}
        {/* Only once it starts to matter — a counter over an empty box is noise. */}
        {body.length > MAX_BODY - 300 && (
          <span className="tnum ms-auto text-xs text-muted-foreground">
            {n(body.length)} / {n(MAX_BODY)}
          </span>
        )}
      </div>
    </div>
  );
}
